import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, Loader2, Check, ImageIcon, ChevronRight, RefreshCw } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const STYLES = [
  { key: "editorial", label: "Editorial" },
  { key: "neon", label: "Neon Nights" },
  { key: "festival", label: "Festival" },
  { key: "prism", label: "Prism" },
  { key: "minimal", label: "Minimal" },
];

const EXAMPLES = [
  "College fest with LED stage, 5000 students, hip-hop headliner in Kolkata",
  "Rooftop after-party with DJ decks, Mumbai skyline at midnight",
  "Sundowner beach party in Goa with acoustic set and bonfire",
  "Corporate summit — modern, minimal, gold accents on black",
];

export default function AIPosterStudio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("editorial");
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    document.title = "AI Poster Studio | Eventom";
    if (user?.role === "organizer" || user?.role === "admin") {
      const url = user.role === "admin" ? "/events?limit=100" : "/organizer/events";
      api.get(url).then(({ data }) => setEvents(data || [])).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="font-display text-4xl">AI Poster Studio</h1>
          <p className="text-muted mt-2">Login as an organiser to use the AI Poster Studio.</p>
          <Link to="/login" className="btn-primary mt-6 inline-flex">Sign in</Link>
        </section>
      </Layout>
    );
  }
  if (user.role !== "organizer" && user.role !== "admin") {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="font-display text-4xl">Organisers only</h1>
          <p className="text-muted mt-2">Create an organiser account to design posters with AI.</p>
          <Link to="/organizer/signup" className="btn-primary mt-6 inline-flex">Become an organiser</Link>
        </section>
      </Layout>
    );
  }

  const generate = async () => {
    if (prompt.trim().length < 6) return toast.error("Describe your event in a sentence or two");
    setBusy(true); setChosen(null);
    try {
      const { data } = await api.post("/ai/poster", { prompt: prompt.trim(), style, count: 3 });
      setUrls(data.urls || []);
      toast.success(`Generated ${data.urls?.length || 0} posters`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI is warming up — try again");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!chosen) return toast.error("Pick a poster first");
    if (!eventId) return toast.error("Choose which event to apply it to");
    setApplying(true);
    try {
      await api.post("/ai/apply-poster", { event_id: eventId, url: chosen });
      toast.success("Poster applied to event banner ✨");
      setTimeout(() => nav(`/events/${eventId}`), 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not apply");
    } finally { setApplying(false); }
  };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="ai-poster-studio">
        <div className="chip !text-magenta !border-magenta/40 mb-3"><Wand2 className="w-3 h-3" /> AI Poster Studio</div>
        <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">Design a poster<br className="hidden md:block"/> <span className="grad-text-hot">in one prompt.</span></h1>
        <p className="mt-3 text-muted max-w-xl">Nano Banana + your words = three ready-to-post 4:5 event posters. Pick your favourite and apply it as the event banner instantly.</p>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 mt-8">
          {/* Prompt column */}
          <div className="glass rounded-3xl p-5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-lime">Describe your event</label>
            <textarea data-testid="poster-prompt" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Rooftop after-party in Kolkata with DJ decks and neon lights"
              className="input-dark !mt-1 resize-none" />

            <div className="mt-3 flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setPrompt(ex)} className="chip !text-[10px] hover:!text-lime" data-testid={`poster-example-${ex.slice(0,10)}`}>
                  {ex.length > 40 ? ex.slice(0, 40) + "…" : ex}
                </button>
              ))}
            </div>

            <label className="text-[10px] font-mono uppercase tracking-widest text-skyx mt-4 block">Style</label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="poster-style">
              {STYLES.map((s) => (
                <button key={s.key} onClick={() => setStyle(s.key)}
                  data-testid={`poster-style-${s.key}`}
                  className={`chip !text-[10px] ${style === s.key ? "!bg-magenta !text-white !border-magenta" : ""}`}>{s.label}</button>
              ))}
            </div>

            <button disabled={busy} onClick={generate} data-testid="poster-generate" className="btn-primary w-full justify-center mt-5">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating 3 posters…</> : <><Sparkles className="w-4 h-4" /> Generate 3 posters</>}
            </button>
            <div className="text-[10px] font-mono text-muted mt-2">Each generation uses Emergent LLM credits.</div>

            {chosen && (
              <div className="mt-5 border-t border-white/10 pt-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-2">Apply to an event</div>
                <select data-testid="poster-event" value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-dark !text-xs">
                  <option value="" className="bg-bg">Select event…</option>
                  {events.map((ev) => <option key={ev.id} value={ev.id} className="bg-bg">{ev.title} · {ev.city || "—"}</option>)}
                </select>
                <button disabled={applying || !eventId} onClick={apply} data-testid="poster-apply" className="btn-lime w-full justify-center mt-3">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply as event banner
                </button>
              </div>
            )}
          </div>

          {/* Results column */}
          <div>
            {urls.length === 0 && !busy ? (
              <div className="glass rounded-3xl p-10 text-center h-full flex flex-col items-center justify-center gap-3" data-testid="poster-empty">
                <ImageIcon className="w-14 h-14 text-white/25" />
                <div className="font-display text-2xl">Your posters land here.</div>
                <div className="text-sm text-muted max-w-sm">Write a prompt, pick a style and hit Generate. Ready in about 20 seconds.</div>
              </div>
            ) : busy ? (
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/5] rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center animate-pulse"><Loader2 className="w-6 h-6 animate-spin text-magenta" /></div>)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="poster-results">
                  {urls.map((u, i) => (
                    <button key={i} onClick={() => setChosen(u)} data-testid={`poster-result-${i}`}
                      className={`relative rounded-3xl overflow-hidden border-2 transition-all ${chosen === u ? "border-lime scale-[1.02]" : "border-transparent hover:border-white/30"}`}>
                      <img src={u} alt={`AI poster ${i + 1}`} className="w-full aspect-[4/5] object-cover" />
                      {chosen === u && (
                        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-lime text-ink flex items-center justify-center"><Check className="w-4 h-4" /></div>
                      )}
                      <div className="absolute bottom-2 left-2 text-[9px] font-mono uppercase tracking-widest bg-black/60 text-white px-2 py-0.5 rounded-full">Option {i + 1}</div>
                    </button>
                  ))}
                </div>
                <button onClick={generate} data-testid="poster-regenerate" className="btn-ghost mt-3 !text-xs"><RefreshCw className="w-3.5 h-3.5" /> Try 3 more variations</button>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
